import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { clientThreadSummaryQuery } from '@/lib/threadSummaryQueries';

export interface ClientThread {
  id: string;
  client_id: string;
  title: string | null;
  status: 'new' | 'active' | 'resolved';
  source_path: string | null;
  created_at: string;
  updated_at: string;
  client_last_read_at: string | null;
  coach_last_read_at?: string | null;
}

export interface ClientMessage {
  id: string;
  thread_id: string;
  sender_role: 'client' | 'coach';
  sender_id: string;
  message_text: string;
  voice_url: string | null;
  button_label: string | null;
  button_route: string | null;
  button_type: string | null;
  created_at: string;
  reply_to_id?: string | null;
  edited_at?: string | null;
  reply_to?: {
    id: string;
    sender_role: 'client' | 'coach';
    message_text: string;
    voice_url: string | null;
  } | null;
}

export function useClientThreads() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<ClientThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasUnread, setHasUnread] = useState(false);
  const [unreadByThread, setUnreadByThread] = useState<Record<string, boolean>>({});
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchThreads = async () => {
    if (!user) return;
    const { data } = await clientThreadSummaryQuery(supabase, user.id);

    if (data) {
      const typed = data as unknown as ClientThread[];
      setThreads(typed);

      // Unread = latest coach message in a non-resolved thread is newer than client_last_read_at
      const openThreads = typed.filter(t => t.status !== 'resolved');
      if (openThreads.length === 0) {
        setHasUnread(false);
        setUnreadByThread({});
        setUnreadCount(0);
      } else {
        const latestByThread = new Map(data.map(t => [t.id, t.latest_messages?.[0]?.created_at]));
        const entries = openThreads.map(t => {
            const latestCoach = latestByThread.get(t.id);
            if (!latestCoach) return [t.id, false] as const;
            const lastRead = t.client_last_read_at;
            const isUnread = !lastRead || new Date(latestCoach) > new Date(lastRead);
            return [t.id, isUnread] as const;
          });
        const map = Object.fromEntries(entries);
        setUnreadByThread(map);
        const count = Object.values(map).filter(Boolean).length;
        setHasUnread(count > 0);
        setUnreadCount(count);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchThreads();

    if (!user) return;

    const channel = supabase
      .channel(`client-threads-changes-${user.id}-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_threads',
        filter: `client_id=eq.${user.id}`,
      }, () => {
        fetchThreads();
      })
      .subscribe();

    // Also re-evaluate when any new message lands in one of the user's threads
    const msgChannel = supabase
      .channel(`client-thread-messages-watch-${user.id}-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'client_messages',
      }, () => {
        fetchThreads();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(msgChannel);
    };
  }, [user]);

  const markThreadRead = async (threadId: string) => {
    if (!user) return;
    const now = new Date().toISOString();
    // Optimistic update
    setThreads(prev => prev.map(t => t.id === threadId ? { ...t, client_last_read_at: now } : t));
    setUnreadByThread(prev => {
      const next = { ...prev, [threadId]: false };
      const count = Object.values(next).filter(Boolean).length;
      setHasUnread(count > 0);
      setUnreadCount(count);
      return next;
    });
    await supabase
      .from('client_threads')
      .update({ client_last_read_at: now })
      .eq('id', threadId)
      .eq('client_id', user.id);
  };

  return { threads, loading, hasUnread, unreadCount, unreadByThread, refetch: fetchThreads, markThreadRead };
}

export function useThreadMessages(threadId: string | null) {
  const [messages, setMessages] = useState<ClientMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMessages = async () => {
    if (!threadId) return;
    setLoading(true);
    const { data } = await supabase
      .from('client_messages')
      .select('*, reply_to:reply_to_id(id, sender_role, message_text, voice_url)')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data as unknown as ClientMessage[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMessages();

    if (!threadId) return;

    const channel = supabase
      .channel(`thread-messages-${threadId}-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_messages',
        filter: `thread_id=eq.${threadId}`,
      }, () => {
        fetchMessages();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId]);

  return { messages, loading, refetch: fetchMessages };
}

export function useActiveThread() {
  // Kept for backwards compatibility: returns the single conversation.
  const { user } = useAuth();
  const [activeThread, setActiveThread] = useState<(ClientThread & { lastCoachMessage?: ClientMessage }) | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!user) return;
      const { data: threads } = await supabase
        .from('client_threads')
        .select('*')
        .eq('client_id', user.id)
        .eq('status', 'active')
        .order('updated_at', { ascending: false })
        .limit(1);

      if (threads && threads.length > 0) {
        const thread = threads[0] as unknown as ClientThread;
        // Get last coach message
        const { data: msgs } = await supabase
          .from('client_messages')
          .select('*')
          .eq('thread_id', thread.id)
          .eq('sender_role', 'coach')
          .order('created_at', { ascending: false })
          .limit(1);

        setActiveThread({
          ...thread,
          lastCoachMessage: msgs?.[0] as unknown as ClientMessage || undefined,
        });
      } else {
        setActiveThread(null);
      }
      setLoading(false);
    };

    fetch();

    if (!user) return;

    const channel = supabase
      .channel(`active-thread-watch-${user.id}-${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_threads',
        filter: `client_id=eq.${user.id}`,
      }, () => {
        fetch();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return { activeThread, loading };
}

/**
 * The single, continuous conversation between this client and Phil.
 * Creates it lazily the first time the client opens the chat.
 */
export function useClientConversation() {
  const { user } = useAuth();
  const [thread, setThread] = useState<ClientThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ensure = async () => {
      if (!user) return;
      setLoading(true);
      const { data, error: selErr } = await supabase
        .from('client_threads')
        .select('*')
        .eq('client_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1);

      if (cancelled) return;

      if (selErr) {
        setError(selErr.message);
        setLoading(false);
        return;
      }

      let existing = data?.[0] as unknown as ClientThread | undefined;

      if (!existing) {
        const { data: created, error: insErr } = await supabase
          .from('client_threads')
          .insert({ client_id: user.id, status: 'active', title: 'Conversation with Phil' })
          .select()
          .single();
        if (cancelled) return;
        if (insErr) {
          // Another tab may have created it in the meantime
          const { data: retry } = await supabase
            .from('client_threads')
            .select('*')
            .eq('client_id', user.id)
            .limit(1);
          existing = retry?.[0] as unknown as ClientThread | undefined;
          if (!existing) setError(insErr.message);
        } else {
          existing = created as unknown as ClientThread;
        }
      }

      setThread(existing ?? null);
      setLoading(false);
    };

    ensure();
    return () => { cancelled = true; };
  }, [user]);

  const markRead = async () => {
    if (!thread || !user) return;
    const now = new Date().toISOString();
    setThread(prev => (prev ? { ...prev, client_last_read_at: now } : prev));
    await supabase
      .from('client_threads')
      .update({ client_last_read_at: now })
      .eq('id', thread.id)
      .eq('client_id', user.id);
  };

  return { thread, loading, error, markRead };
}
